import { ImageWidget } from "~/types/widgets";
import Image from "~/components/ui/Image";
import { useId } from "react";
import Slider from "~/components/ui/Slider";
import SliderJS from "~/components/ui/SliderJS";
import Icon from "~/components/ui/Icon";
interface Props {
  title: string;
  subtitle?: string;
  highlights: HighlightCard[];
  /**
   * @title Margin Top
   * @description Add margin to the top of the section
   */
}

interface HighlightCard {
  title: string;
  cardTitle?: string;
  image: ImageWidget;
  /**
   * @title Vídeo Mobile
   * @description URL do vídeo para dispositivos móveis (opcional). Usado apenas quando "Tipo de Mídia" for "video"
   */
  videoMobile?: string;
  /**
   * @title Vídeo Desktop
   * @description URL do vídeo para desktop (opcional). Usado apenas quando "Tipo de Mídia" for "video"
   */
  videoDesktop?: string;
  /**
   * @title Tipo de Mídia
   * @description Escolha se deseja exibir uma imagem ou vídeo neste card
   * @default image
   */
  mediaType?: "image" | "video";
  href?: string;
}

/**
 * `sizes` do card desktop a partir do nº real de highlights — a seção
 * desktop renderiza `count` cards lado a lado dentro de um container
 * `px-8` (32px de padding em cada lado = 64px), sem wrap. Sem isso, o
 * `<Image>` cai no fallback `50vw` (hardcoded no componente base) e baixa
 * a variante 2x mesmo quando a caixa real tem 1/4 do viewport.
 */
export function getHighlightsDesktopSizes(count: number): string {
  return `calc((100vw - 64px) / ${count})`;
}

export function LoadingFallback() {
  return (
    <>
      {/* Mobile skeleton */}
      <section className="flex flex-col items-start md:hidden mt-5">
        <div className="skeleton animate-pulse h-6 w-40 mb-1 ml-4 rounded" />
        <div className="skeleton animate-pulse h-3 w-56 mb-3 ml-4 rounded" />
        <div className="flex gap-0 w-full overflow-hidden px-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="w-1/2 shrink-0 flex flex-col items-center px-1">
              <div className="skeleton animate-pulse w-40 h-[230px]" />
              <div className="skeleton animate-pulse h-3 w-24 mt-3 rounded" />
            </div>
          ))}
        </div>
      </section>

      {/* Desktop skeleton */}
      <section className="hidden px-8 md:block mt-6">
        <div className="flex flex-col justify-start items-center w-full">
          <div className="skeleton animate-pulse h-6 w-48 mb-1 self-start rounded" />
          <div className="skeleton animate-pulse h-3 w-72 mb-8 self-start rounded" />
          <div className="flex flex-row justify-between gap-2 w-full">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex flex-col items-start">
                <div className="skeleton animate-pulse w-[436px] h-[546px]" />
                <div className="skeleton animate-pulse h-4 w-32 mt-4 rounded" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function HighlightsSection({ title, subtitle, highlights }: Props) {
  const id = useId();

  function MediaComponent({
    highlight,
    isMobile = false,
    className = "",
  }: {
    highlight: HighlightCard;
    isMobile?: boolean;
    className?: string;
  }) {
    const { image, videoMobile, videoDesktop, mediaType, title } = highlight;

    if (mediaType === "video") {
      return (
        <>
          {/* Vídeo Mobile */}
          {videoMobile && (
            <video
              className={`md:hidden group-hover:brightness-[0.8] transition-all duration-300 object-cover w-40 h-[230px] ${className}`}
              src={videoMobile}
              autoPlay
              muted
              loop
              playsInline
            />
          )}

          {/* Vídeo Desktop */}
          {videoDesktop && (
            <video
              className={`hidden md:block group-hover:brightness-[0.8] transition-all duration-300 object-cover w-[436px] h-[546px] ${className}`}
              src={videoDesktop}
              autoPlay
              muted
              loop
              playsInline
            />
          )}

          {/* Fallback: se não houver vídeo específico, usa o que estiver disponível */}
          {!videoMobile && !videoDesktop && (videoMobile || videoDesktop) && (
            <video
              className={`group-hover:brightness-[0.8] transition-all duration-300 object-cover ${
                isMobile ? "w-[160px] h-[230px]" : "w-[436px] h-[546px]"
              } ${className}`}
              src={videoMobile || videoDesktop}
              autoPlay
              muted
              loop
              playsInline
            />
          )}
        </>
      );
    }

    // Renderiza imagem por padrão
    return (
      <>
        {/* Imagem Mobile — item ocupa 50% do carousel (Slider.Item w-[50%]) */}
        <Image
          loading="lazy"
          fetchPriority="low"
          width={160}
          height={230}
          src={image}
          alt={title}
          sizes="50vw"
          className={`md:hidden group-hover:brightness-[0.8] transition-all duration-300 w-40 h-full object-contain ${className}`}
        />

        {/* Imagem Desktop — N cards lado a lado, ver getHighlightsDesktopSizes */}
        <Image
          loading="lazy"
          fetchPriority="low"
          width={436}
          height={546}
          src={image}
          alt={title}
          sizes={getHighlightsDesktopSizes(highlights.length)}
          className={`hidden md:block group-hover:brightness-[0.8] transition-all duration-300 w-[436px] h-full object-cover ${className}`}
        />
      </>
    );
  }

  function Dots({ highlights }: { highlights: HighlightCard[] }) {
    return (
      <>
        <style
          dangerouslySetInnerHTML={{
            __html: `
            @property --dot-progress {
              syntax: '<percentage>';
              inherits: false;
              initial-value: 0%;
            }
            `,
          }}
        />
        <ul className="carousel col-span-full z-10 row-start-4 w-auto h-6 justify-self-center justify-center mt-0 xl:justify-start bg-none">
          {highlights?.map((_, index) => (
            <li className="carousel-item pl-1 lg:pl-2 focus:bg-black lg:[&:not(:nth-child(5n-4))]:hidden">
              <Slider.Dot index={index}>
                <div className="hidden group-disabled:flex">
                  <Icon id={"ActiveDot"} size={12} />
                </div>
                <div className="flex group-disabled:hidden">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="11"
                      height="11"
                      rx="5.5"
                      stroke="#CCCCCC"
                    />
                    <rect
                      opacity="0.16"
                      x="2"
                      y="2"
                      width="8"
                      height="8"
                      rx="4"
                      fill="#434343"
                    />
                  </svg>
                </div>
              </Slider.Dot>
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <>
      {/* Mobile Version */}
      <section className="flex flex-col items-start md:hidden mt-5">
        {title && (
          <h2 className="font-['Poppins'] not-italic font-normal text-[22px] leading-[32px] uppercase text-[#1A1A1A] mt-5 mb-1 px-4">
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="font-['Poppins'] not-italic font-normal text-[11px] leading-[22px] text-[#757575] mb-3 lg:mb-5 text-start px-4">
            {subtitle}
          </p>
        )}
        <div id={id} className="h-full flex flex-col justify-center">
          <Slider className="carousel carousel-center sm:carousel-end gap-0 col-span-full row-start-2 row-end-5 h-full w-full md:gap-6">
            {highlights?.map((highlight, index) => {
              return (
                <Slider.Item
                  index={index}
                  className="carousel-item w-[50%] flex justify-center"
                >
                  <a
                    href={highlight.href}
                    className="text-start group flex flex-col h-full"
                  >
                    <MediaComponent
                      highlight={highlight}
                      isMobile={true}
                      className="group-hover:opacity-70"
                    />
                    {highlight.cardTitle && (
                      <h3 className="font-['Poppins'] not-italic font-medium text-base leading-[20px] text-[#1A1A1A] mt-5">
                        {highlight.cardTitle}
                      </h3>
                    )}
                    <p
                      className={`font-['Poppins'] not-italic font-normal tracking-[1.08px] text-sm lg:text-base leading-[26px] text-[#1A1A1A] uppercase ${
                        highlight.cardTitle ? "mt-1 lg:mt-2" : "pt-2"
                      }`}
                    >
                      {highlight.title}
                    </p>
                  </a>
                </Slider.Item>
              );
            })}
          </Slider>
          <div className="col-span-full row-start-2 flex justify-center mt-0">
            <Dots highlights={highlights} />
          </div>
          <SliderJS rootId={id} infinite />
        </div>
      </section>

      {/* Desktop Version */}
      <section className="hidden px-8 md:block mt-6">
        <div className="flex flex-col justify-start items-center">
          {title && (
            <h2 className="w-full font-['Poppins'] not-italic font-medium text-[22px] lg:text-2xl leading-[32px] uppercase text-[#1A1A1A] mt-5 mb-1">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="w-full font-['Poppins'] not-italic font-normal tex-[11px] lg:text-[13px] leading-[24px] text-[#666666] mb-8">
              {subtitle}
            </p>
          )}
          {highlights && (
            <ul className="flex flex-row justify-between gap-2 w-full">
              {highlights.map((highlight: HighlightCard) => (
                <li>
                  <a
                    href={highlight.href}
                    className="group font-['Poppins'] not-italic font-normal text-lg leading-[26px] text-black mt-5 text-start flex flex-col"
                  >
                    <MediaComponent
                      highlight={highlight}
                      isMobile={false}
                    />
                    <div className="flex flex-col">
                      {highlight.cardTitle && (
                        <h3 className="font-['Poppins'] not-italic font-medium text-xs lg:text-lg leading-[24px] text-[#1A1A1A] mt-6">
                          {highlight.cardTitle}
                        </h3>
                      )}
                      <p
                        className={`font-['Poppins'] not-italic font-normal tracking-[1.08px] text-sm lg:text-base leading-[26px] text-[#1A1A1A] uppercase group-hover:text-[#7A7A7A] ${
                          highlight.cardTitle ? "mt-3" : "pt-2.5"
                        }`}
                      >
                        {highlight.title}
                      </p>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}

export default HighlightsSection;
